<template>
  <BCard>
    <!-- Header -->
    <div class="mb-[1.75rem]">
      <h1 class="text-black text-2xl font-medium mb-[.5rem]">
        Churn Request Overview
      </h1>
      <p class="m-0 text-[#737373]">
        Kelola dan pantau proses pengunduran diri dan penggantian talent.
      </p>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-5 gap-[1rem] mb-[1.75rem]">
      <div
        v-for="(card, i) in summaryCards"
        :key="i"
        class="summary-card"
      >
        <div class="z-10 flex flex-col">
          <span class="text-black text-[14px] mb-[.25rem]">{{ card.label }}</span>
          <div class="text-black text-[20px] font-medium mt-[4px]">
            {{ card.value }}<span
              v-if="card.suffix"
              class="text-[20px] font-medium ml-[4px]"
            >{{ card.suffix }}</span>
          </div>
          <span
            v-if="card.subLabel"
            class="text-[#A3A3A3] text-[12px] mt-[1.25rem]"
          >{{ card.subLabel }}</span>
        </div>
        <div
          class="summary-card-bg"
          :style="{ background: card.circleColor }"
        />
      </div>
    </div>

    <div class="border rounded-2xl p-[1rem] bg-[#FAFAFA]">
      <!-- Filter Bar -->
      <div class="flex items-center gap-[1rem] mb-[1.75rem]">
        <span class="text-black text-[18px] font-semibold me-auto whitespace-nowrap">Recent Requests</span>
        <div class="d-flex rounded-lg align-items-center border search-bar max-w-[250px] p-[2px]">
          <span
            class="k-search-normal-1 h-100 font-bold align-middle ms-[.75rem]"
          />
          <BFormInput
            v-model="searchKey"
            placeholder="Cari Partner atau Talent"
            class="border-0"
            @input="onSearch"
          />
        </div>
        <div class="manage-partner-tool-container ps-[.75rem] group">
          <span class="k-add-circle" />
          <VSelect
            v-model="selectedTipe"
            :reduce="e => e.id"
            :options="tipeOptions"
            :clearable="false"
            placeholder="Tipe Request"
            label="name"
            class="plain-vue-select solid-placeholder-vue-select w-fit min-w-[125px]"
            @input="validateParams()"
          >
            <template #open-indicator>
              <span />
            </template>
            <template #selected-option="{ name }">
              <span class="text-black font-medium whitespace-nowrap">{{ name }}</span>
            </template>
            <template #option="{ name }">
              <span class="whitespace-nowrap">{{ name }}</span>
            </template>
          </VSelect>
        </div>
        <div class="manage-partner-tool-container ps-[.75rem] group">
          <span class="k-add-circle" />
          <VSelect
            v-model="selectedStatus"
            :reduce="e => e.id"
            :options="statusOptions"
            :clearable="false"
            placeholder="Status Request"
            label="name"
            class="plain-vue-select solid-placeholder-vue-select w-fit group-focus-within:min-w-[250px] min-w-[150px]"
            @input="validateParams()"
          >
            <template #open-indicator>
              <span />
            </template>
            <template #selected-option="{ name }">
              <span class="text-black font-medium whitespace-nowrap">{{ name }}</span>
            </template>
            <template #option="{ name }">
              <span class="whitespace-nowrap">{{ name }}</span>
            </template>
          </VSelect>
        </div>
        <BButton
          variant="outline-primary"
          class="reset-btn"
          :disabled="!(searchKey || selectedTipe || selectedStatus)"
          @click="() => {
            resetFilter()
            validateParams()
          }"
        >
          <div class="inline-block flex items-center">
            <feather-icon
              icon="XIcon"
              size="16"
            />
            <span class="ms-[.75rem]">Reset</span>
          </div>
        </BButton>
      </div>

      <!-- Table -->
      <ChurnRequestTable
        ref="churnRequestTable"
        :items="items"
        :loading="isLoadingData"
      />
    </div>
  </BCard>
</template>

<style scoped>
::v-deep .plain-vue-select .vs__dropdown-toggle {
  border: none !important;
}

::v-deep .solid-placeholder-vue-select .vs__search::placeholder {
  color: black;
  font-weight: 500 !important;
  opacity: 1;
}
</style>

<script>
import VSelect from 'vue-select'
import '@core/scss/vue/libs/vue-select.scss'
import { komtimAxiosIns } from '@/libs/axios'
import {
  tipeRequestOptions,
  statusRequestOptions,
  showError,
} from './config'
import ChurnRequestTable from './Table.vue'

export default {
  components: { VSelect, ChurnRequestTable },
  data() {
    return {
      items: [],
      limit: 10,
      offset: 0,
      isLastData: false,
      isLoadingData: false,
      searchKey: null,
      selectedTipe: null,
      selectedStatus: null,
      tipeOptions: tipeRequestOptions,
      statusOptions: statusRequestOptions,
      scrollObserver: null,

      // Summary card data
      summaryCards: [
        {
          label: 'Total Off Talent',
          value: 0,
          suffix: null,
          subLabel: 'Bulan ini',
          circleColor: 'linear-gradient(90deg, #BBF7D0 0%, #22C55E 100%)',
        },
        {
          label: 'Total Ganti Talent',
          value: 0,
          suffix: null,
          subLabel: 'Bulan ini',
          circleColor: 'linear-gradient(90deg, #A5F3FC 0%, #5DD5F2 100%)',
        },
        {
          label: 'On Progress',
          value: 0,
          suffix: null,
          subLabel: null,
          circleColor: 'linear-gradient(90deg, #FFD3B6 0%, #FFA271 100%)',
        },
        {
          label: 'Avg. Resolution',
          value: 0,
          suffix: 'Hari',
          subLabel: null,
          circleColor: 'linear-gradient(90deg, #FDE68A 0%, #F59E0B 100%)',
        },
        {
          label: 'Churn Rate',
          value: '0%',
          suffix: null,
          subLabel: null,
          circleColor: 'linear-gradient(90deg, #C7C8F8 0%, #7779E4 100%)',
        },
      ],
    }
  },
  mounted() {
    this.getMetricsData()
    this.validateParams()
    this.initScrollObserver()
  },
  beforeDestroy() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect()
    }
  },
  methods: {
    // ── Filter methods ─────────────────────────
    resetFilter() {
      this.searchKey = null
      this.selectedTipe = null
      this.selectedStatus = null
    },
    /**
     * Validate stored search params.
     * Restore previous search keyword or load default data.
     */
    validateParams() {
      if (this.searchKey || this.selectedTipe || this.selectedStatus) {
        this.onSearch()
        return
      }
      this.items = []
      this.offset = 0
      this.getListData()
    },
    /**
     * Debounced search handler.
     * Reload data based on current filters.
     */
    onSearch: _.debounce(async function search() {
      this.offset = 0
      this.isLastData = false
      this.items = []
      await this.getListData()
    }, 1000),

    // ── Helper methods ─────────────────────────
    /**
     * Format number to percentage with max 2 decimal places rounded to nearest.
     */
    formatPercentage(value) {
      return `${Number(Number(value).toFixed(2))}%`
    },

    // ── API ────────────────────────────────────
    /**
     * Get churn request metrics data
     */
    async getMetricsData() {
      try {
        const url = '/v1/partner_assignment_requests/metrics'
        const { data: { data } } = await komtimAxiosIns.get(url)
        if (data) {
          this.summaryCards[0].value = data.total_off_talent ?? 0
          this.summaryCards[1].value = data.total_switch_talent ?? 0
          this.summaryCards[2].value = data.total_on_progress ?? 0
          this.summaryCards[3].value = data.average_resolution ?? 0
          this.summaryCards[4].value = this.formatPercentage(data.off_talent_request_rate)
        }
      } catch {
        showError({ title: 'Terjadi Kesalahan' })
      }
    },
    /**
     * Get churn request list
     */
    async getListData() {
      try {
        this.isLoadingData = true
        const params = {
          offset: this.offset,
          limit: this.limit,
          search: this.searchKey,
          request_type_code: this.selectedTipe,
          status_code: this.selectedStatus,
        }
        if (params.status_code === 'WAITING_PAYMENT') {
          params.status_code = 'IN_PROGRESS'
          params.invoice_status = 'UNPAID'
        }
        const url = '/v1/partner_assignment_requests'

        const { data: { data } } = await komtimAxiosIns.get(url, { params })
        if (data.length < this.limit) {
          this.isLastData = true
        } else {
          this.isLastData = false
        }
        this.items.push(...data)
        this.offset += data.length
      } catch {
        if (this.offset === 0) {
          this.items = []
        }
      } finally {
        this.isLoadingData = false
      }
    },

    // ── Infinite Scroll (IntersectionObserver) ─
    initScrollObserver() {
      const tableComponent = this.$refs.churnRequestTable
      if (!tableComponent) return

      const sentinel = tableComponent.$refs.scrollSentinel
      const container = tableComponent.$refs.tableContainer
      if (!sentinel || !container) return

      this.scrollObserver = new IntersectionObserver(
        entries => {
          const entry = entries[0]
          if (entry.isIntersecting && !this.isLastData && !this.isLoadingData) {
            this.getListData()
          }
        },
        { root: container, threshold: 0 },
      )
      this.scrollObserver.observe(sentinel)
    },
  },
}
</script>

<style scoped>
/* ── Summary Cards ──────────────────────── */
.summary-card {
  position: relative;
  padding: 4px;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #E5E5E5;
}

.summary-card,
.summary-card > * {
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  border: 1px solid rgba(229, 229, 229, 0.8);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.06);
  padding: 8px;
  height: 100%;
}

.summary-card-bg {
  position: absolute;
  top: 0%;
  left: 0%;
  width: 100px;
  height: 100px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  filter: blur(28px);
  -webkit-filter: blur(28px);
  opacity: 0.65;
  pointer-events: none;
  z-index: 0;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  animation: rectangle-orbit 8s linear infinite;
}

@keyframes rectangle-orbit {
  0% {
    top: 0%;
    left: 0%;
  }
  35% {
    top: 0%;
    left: 100%;
  }
  50% {
    top: 100%;
    left: 100%;
  }
  85% {
    top: 100%;
    left: 0%;
  }
  100% {
    top: 0%;
    left: 0%;
  }
}

/* ── Filter bar (manage-partner style) ─── */
.manage-partner-tool-container {
  display: flex;
  align-items: center;
  border-radius: 10px;
  color: black !important;
  border: 2px dashed #E5E5E5 !important;
}

.reset-btn {
  border-radius: 10px;
  padding: .75rem 1rem !important;
}
</style>
